import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, Facebook, Twitter, Trash2, RefreshCw, BarChart3, MessageCircleQuestion, TrendingUp } from "lucide-react";

const TYPE_META = {
  poll: { label: "Poll", icon: BarChart3, tone: "#2f7bed" },
  question: { label: "Question", icon: MessageCircleQuestion, tone: "#16a34a" },
  alert: { label: "Pattern Alert", icon: TrendingUp, tone: "#f6a21c" },
};
const ALL_TYPES = ["poll", "question", "alert"];

const fullText = (it) => {
  let t = it.text;
  if (it.type === "poll" && it.options?.length) t += "\n\n" + it.options.map((o, i) => `${i + 1}. ${o}`).join("\n");
  if (it.hashtags?.length) t += "\n\n" + it.hashtags.join(" ");
  return t;
};

export function SocialStudioCard() {
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState(ALL_TYPES);
  const [count, setCount] = useState(4);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/social-studio/config").then((r) => { setCfg(r.data); setTypes(r.data.enabled_types || ALL_TYPES); setCount(r.data.count || 4); }).catch(() => {});
    api.get("/social-studio/items").then((r) => setItems(r.data.items || [])).catch(() => {});
  };
  useEffect(load, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/social-studio/generate", { types, count });
      setItems((prev) => [...data.items, ...prev]);
      toast.success(`Generated ${data.items.length} posts`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); } finally { setBusy(false); }
  };
  const copy = async (it) => {
    try { await navigator.clipboard.writeText(fullText(it)); toast.success(it.type === "poll" ? "Poll copied — paste into the native poll composer" : "Copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const post = async (it, platform) => {
    try {
      const { data } = await api.post("/social-studio/post", { id: it.id, platforms: [platform] });
      const r = data.results[platform];
      if (r?.ok) { toast.success(`Posted to ${platform === "facebook" ? "Facebook" : "X"}`); setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, posted: data.posted } : x)); }
      else toast.error(r?.error || "Post failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
  };
  const remove = async (it) => {
    try { await api.delete(`/social-studio/items/${it.id}`); setItems((prev) => prev.filter((x) => x.id !== it.id)); }
    catch { toast.error("Delete failed"); }
  };

  if (!cfg) return null;
  const toggleType = (t) => setTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  return (
    <div className="glass rounded-sm border border-[#2f7bed]/40 p-6" data-testid="social-studio-card">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-[#7fb2ff]" />
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Pattern Watch &amp; Social Studio</div>
      </div>
      <p className="mt-1 text-sm text-gray-300">Spots what's changing in the Georgia forecast and writes engagement-ready posts (powered by Claude). Copy them, or one-tap post the non-poll ones to your connected accounts.</p>

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-center gap-4 rounded-sm border border-white/10 bg-black/30 px-4 py-3">
        <div className="flex flex-wrap gap-3">
          {ALL_TYPES.map((t) => {
            const M = TYPE_META[t];
            return (
              <label key={t} className="flex items-center gap-2 text-sm text-gray-200">
                <input type="checkbox" data-testid={`ss-type-${t}`} checked={types.includes(t)} onChange={() => toggleType(t)} className="h-4 w-4 accent-[#2f7bed]" />
                {M.label}
              </label>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-oswald text-[11px] uppercase tracking-widest text-gray-400">How many</span>
          <select data-testid="ss-count" value={count} onChange={(e) => setCount(Number(e.target.value))} className="rounded-sm border border-white/15 bg-black/40 px-2 py-1.5 text-sm text-white focus:border-[#2f7bed] focus:outline-none">
            {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button data-testid="ss-generate" onClick={generate} disabled={busy || !types.length}
          className="ml-auto flex items-center gap-2 rounded-sm bg-[#2f7bed] px-4 py-2 font-oswald text-sm font-bold uppercase tracking-widest text-white hover:bg-[#3f8bfd] disabled:opacity-40">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Thinking…" : "Generate posts"}
        </button>
      </div>
      <div className="mt-2 flex gap-4 text-xs">
        <span className={cfg.facebook_connected ? "text-emerald-400" : "text-gray-500"}>● Facebook {cfg.facebook_connected ? "connected" : "not connected"}</span>
        <span className={cfg.twitter_connected ? "text-emerald-400" : "text-gray-500"}>● X {cfg.twitter_connected ? "connected" : "not connected"}</span>
      </div>

      {/* Items */}
      <div className="mt-4 grid gap-3 lg:grid-cols-2" data-testid="ss-items">
        {items.length === 0 && <div className="col-span-full rounded-sm border border-dashed border-white/15 px-4 py-8 text-center text-sm text-gray-500">No posts yet — hit "Generate posts" to create some.</div>}
        {items.map((it) => {
          const M = TYPE_META[it.type] || TYPE_META.question;
          const Icon = M.icon;
          return (
            <div key={it.id} className="flex flex-col rounded-sm border border-white/10 bg-black/30 p-4" data-testid={`ss-item-${it.id}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 rounded-[3px] px-2 py-0.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-white" style={{ background: M.tone }}>
                  <Icon className="h-3.5 w-3.5" /> {M.label}
                </span>
                <button onClick={() => remove(it)} data-testid={`ss-del-${it.id}`} className="text-gray-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
              <p className="mt-2 flex-1 whitespace-pre-wrap text-sm leading-snug text-white">{it.text}</p>
              {it.type === "poll" && it.options?.length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {it.options.map((o, i) => <div key={i} className="rounded-[3px] border border-white/15 bg-white/5 px-3 py-1 text-sm text-gray-200">{o}</div>)}
                </div>
              )}
              {it.hashtags?.length > 0 && <div className="mt-2 text-xs font-semibold text-[#7fb2ff]">{it.hashtags.join(" ")}</div>}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
                <button onClick={() => copy(it)} data-testid={`ss-copy-${it.id}`} className="flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-xs uppercase tracking-widest text-white hover:border-[#2f7bed]"><Copy className="h-3.5 w-3.5" /> Copy</button>
                {it.type !== "poll" && (
                  <>
                    <button onClick={() => post(it, "facebook")} disabled={!cfg.facebook_connected} data-testid={`ss-fb-${it.id}`} className="flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-xs uppercase tracking-widest text-white hover:border-[#1877f2] disabled:opacity-30"><Facebook className="h-3.5 w-3.5" /> {it.posted?.facebook ? "Posted" : "Facebook"}</button>
                    <button onClick={() => post(it, "twitter")} disabled={!cfg.twitter_connected} data-testid={`ss-tw-${it.id}`} className="flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-xs uppercase tracking-widest text-white hover:border-white/60 disabled:opacity-30"><Twitter className="h-3.5 w-3.5" /> {it.posted?.twitter ? "Posted" : "Post to X"}</button>
                  </>
                )}
                {it.type === "poll" && <span className="text-xs text-gray-500">Polls: copy &amp; paste into the platform's poll composer</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
